import React, { useState, useEffect, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X, Upload, Loader2, Plus, UserPlus, Trash2 } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';
import { useAppStore } from '../../store';
import { ExpressSample, ExpressSampleItem } from '../../types';
import EntityModal from './EntityModal';
import ErrorModal from './ErrorModal';
import ConfirmCloseModal from './ConfirmCloseModal';
import ConfirmModal from './ConfirmModal';
import { supabase } from '../../lib/supabase';

interface ExpressSampleModalProps {
  isOpen: boolean;
  onClose: () => void;
  sampleId?: string | null;
}

console.log('ExpressSampleModal – currentUser →', useAppStore.getState().currentUser);

// Define localStorage keys
const STORAGE_KEYS = {
  APPROVED_FILE: 'express_sample_approved_file',
  DTF_FILE: 'express_sample_dtf_file',
  PAYMENT_RECEIPT: 'express_sample_payment_receipt',
  SHIPPING_LABEL: 'express_sample_shipping_label',
  FORM_DATA: 'express_sample_form_data',
  ITEMS: 'express_sample_items',
  FILE_NAMES: 'express_sample_file_names'
};

const ExpressSampleModal: React.FC<ExpressSampleModalProps> = ({
  isOpen,
  onClose,
  sampleId,
}) => {
  const { 
    clients, 
    expressSamples, 
    expressSampleItems, 
    expressStock,
    addExpressSample, 
    updateExpressSample, 
    addClient, 
    addExpressStockMovement,
    fetchExpressStock,
    fetchExpressSampleItems
  } = useAppStore();
// ¿El usuario es Depósito?
  const { currentUser } = useAppStore();
  const isDeposito = (
    currentUser?.role ?? ''
  )
    .normalize('NFD')              // quita tildes
    .replace(/[\u0300-\u036f]/g, '') // caracteres diacríticos
    .toLowerCase()
    === 'deposito';
  const [isClientModalOpen, setIsClientModalOpen] = useState(false);
  const [isConfirmCloseModalOpen, setIsConfirmCloseModalOpen] = useState(false);
  const [formData, setFormData] = useState<Omit<ExpressSample, 'id' | 'created_at' | 'updated_at'>>({
    client_id: '',
    name: '',
    approved_file: '',
    dtf_file: '',
    delivery_date: '',
    salesperson: 'Mateo',
    payment_receipt: '',
    shipping_label: '',
    status: 'pending',
    comment: '',
    dtf_printed: null,
    dtf_picked_up: null,
    is_printed: false,
    sample_value: null
  });

  const [items, setItems] = useState<(Omit<ExpressSampleItem, 'id' | 'express_sample_id' | 'created_at'> & { locked?: boolean })[]>([]);
  const [originalItems, setOriginalItems] = useState<(Omit<ExpressSampleItem, 'id' | 'express_sample_id' | 'created_at'> & { locked?: boolean })[]>([]);
  const [isUploading, setIsUploading] = useState({
    approved: false,
    dtf: false,
    receipt: false,
    shipping: false
  });
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [formModified, setFormModified] = useState(false);
  const [fileToDelete, setFileToDelete] = useState<{field: string, fileName: string} | null>(null);
  const [isDeleteFileModalOpen, setIsDeleteFileModalOpen] = useState(false);
  const [fileNames, setFileNames] = useState<Record<string, string[]>>({});

  // Check if form is valid
  const isFormValid = () => {
    return (
      formData.client_id !== '' && 
      formData.name !== '' && 
      formData.delivery_date !== '' && 
      formData.approved_file !== '' && 
      items.length > 0
    );
  };

  // Reset form to initial state
  const resetForm = () => {
    setFormData({
      client_id: '',
      name: '',
      approved_file: '',
      dtf_file: '',
      delivery_date: '',
      salesperson: 'Mateo',
      payment_receipt: '',
      shipping_label: '',
      status: 'pending',
      comment: '',
      dtf_printed: null,
      dtf_picked_up: null,
      is_printed: false,
      sample_value: null
    });
    setItems([]);
    setOriginalItems([]);
    setFormModified(false);
    setFileNames({});
  };

  // Handle form changes
  const handleFormChange = (newFormData: typeof formData) => {
    setFormData(newFormData);
    setFormModified(true);
  };

  // Handle sample value change
  const handleSampleValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // If the field is empty, set null, otherwise convert to number
    const numericValue = value === '' ? null : parseFloat(value);
    
    handleFormChange({
      ...formData,
      sample_value: numericValue
    });
  };

  // Load data from localStorage when modal opens
useEffect(() => {
  if (!isOpen) return;

if (!sampleId) {
  // ───────── NUEVA MUESTRA ─────────
  // 1) Recuperar cualquier borrador previo
  const approvedFile   = localStorage.getItem(STORAGE_KEYS.APPROVED_FILE)   || '';
  const dtfFile        = localStorage.getItem(STORAGE_KEYS.DTF_FILE)        || '';
  const paymentReceipt = localStorage.getItem(STORAGE_KEYS.PAYMENT_RECEIPT) || '';
  const shippingLabel  = localStorage.getItem(STORAGE_KEYS.SHIPPING_LABEL)  || '';

  const savedFormData  = localStorage.getItem(STORAGE_KEYS.FORM_DATA);
  const savedItems     = localStorage.getItem(STORAGE_KEYS.ITEMS);
  const savedFileNames = localStorage.getItem(STORAGE_KEYS.FILE_NAMES);

  // 2) Cargar el borrador (si existe)
  if (savedFormData) {
    setFormData(prev => ({
      ...prev,
      ...JSON.parse(savedFormData),
      approved_file:  approvedFile,
      dtf_file:       dtfFile,
      payment_receipt: paymentReceipt,
      shipping_label:  shippingLabel,
    }));
  }

  if (savedItems) {
    setItems(JSON.parse(savedItems));
  }

  // Si no había nada guardado, simplemente dejamos el formulario en blanco,
  // pero **NO** borramos localStorage. Así, los cambios futuros se seguirán guardando.
  return;  // ← ¡importante! salimos aquí porque no hay sampleId
}


  // EDICIÓN DE MUESTRA
  try {
    const approvedFile = localStorage.getItem(STORAGE_KEYS.APPROVED_FILE);
    const dtfFile = localStorage.getItem(STORAGE_KEYS.DTF_FILE);
    const paymentReceipt = localStorage.getItem(STORAGE_KEYS.PAYMENT_RECEIPT);
    const shippingLabel = localStorage.getItem(STORAGE_KEYS.SHIPPING_LABEL);
    const savedFormData = localStorage.getItem(STORAGE_KEYS.FORM_DATA);
    const parsedFormData = savedFormData ? JSON.parse(savedFormData) : null;
    const savedItems = localStorage.getItem(STORAGE_KEYS.ITEMS);
    const parsedItems = savedItems ? JSON.parse(savedItems) : null;
    const savedFileNames = localStorage.getItem(STORAGE_KEYS.FILE_NAMES);
    const parsedFileNames = savedFileNames ? JSON.parse(savedFileNames) : {};

    const sample = expressSamples.find(s => s.id === sampleId);
    const sampleItems = expressSampleItems.filter(i => i.express_sample_id === sampleId);

    if (sample) {
      setFormData({
        client_id: sample.client_id,
        name: sample.name,
        approved_file: sample.approved_file || '',
        dtf_file: sample.dtf_file || '',
        delivery_date: sample.delivery_date,
        salesperson: sample.salesperson,
        payment_receipt: sample.payment_receipt || '',
        shipping_label: sample.shipping_label || '',
        status: sample.status,
        comment: sample.comment || '',
        dtf_printed: sample.dtf_printed || null,
        dtf_picked_up: sample.dtf_picked_up || null,
        is_printed: sample.is_printed || false,
        sample_value: sample.sample_value || null
      });

  // ← NUEVO: cargar nombres originales
  if (sample.file_names) {
    try {
      setFileNames(JSON.parse(sample.file_names));
    } catch (e) { console.error('file_names mal formateado', e); }
  }


      
      const itemsData = sampleItems.map(item => ({
        product_type: item.product_type,
        color: item.color,
        size: item.size,
        quantity: item.quantity,
        locked: true
      }));

      setItems(itemsData);
      setOriginalItems(JSON.parse(JSON.stringify(itemsData)));

      // Solo aplicar localStorage si hay cambios guardados
      if (parsedFormData || parsedItems || parsedFileNames) {
        setFormData(prev => ({
          ...prev,
          ...(parsedFormData || {}),
          approved_file: approvedFile || prev.approved_file,
          dtf_file: dtfFile || prev.dtf_file,
          payment_receipt: paymentReceipt || prev.payment_receipt,
          shipping_label: shippingLabel || prev.shipping_label
        }));

        if (parsedItems && Array.isArray(parsedItems)) {
          setItems(parsedItems);
        }

        if (parsedFileNames && Object.keys(parsedFileNames).length) {
    setFileNames(parsedFileNames);
  }

        setFormModified(true);
      }
    }
  } catch (error) {
    console.error('Error loading data from localStorage:', error);
  }
}, [isOpen, sampleId, expressSamples, expressSampleItems]);


  // Save form data to localStorage when it changes
  useEffect(() => {
    if (isOpen && formModified) {
      try {
        // Save form data without the file URLs (we store those separately)
        const formDataToSave = {
          ...formData,
          approved_file: undefined,
          dtf_file: undefined,
          payment_receipt: undefined,
          shipping_label: undefined,
        };
        
        localStorage.setItem(STORAGE_KEYS.FORM_DATA, JSON.stringify(formDataToSave));
        
        // Save items (without the locked status)
        const itemsToSave = items.map(({ locked, ...item }) => item);
        localStorage.setItem(STORAGE_KEYS.ITEMS, JSON.stringify(itemsToSave));
        
        // Save file URLs separately
        if (formData.approved_file) {
          localStorage.setItem(STORAGE_KEYS.APPROVED_FILE, formData.approved_file);
        }
        
        if (formData.dtf_file) {
          localStorage.setItem(STORAGE_KEYS.DTF_FILE, formData.dtf_file);
        }
        
        if (formData.payment_receipt) {
          localStorage.setItem(STORAGE_KEYS.PAYMENT_RECEIPT, formData.payment_receipt);
        }
        
        if (formData.shipping_label) {
          localStorage.setItem(STORAGE_KEYS.SHIPPING_LABEL, formData.shipping_label);
        }
        
        // Save file names
        localStorage.setItem(STORAGE_KEYS.FILE_NAMES, JSON.stringify(fileNames));
      } catch (error) {
        console.error('Error saving data to localStorage:', error);
        // If there's an error saving, just continue
      }
    }
  }, [formData, items, isOpen, formModified, fileNames]);

  // Clear localStorage and close modal
  const handleConfirmClose = () => {
    // Clear localStorage when modal is closed
    localStorage.removeItem(STORAGE_KEYS.APPROVED_FILE);
    localStorage.removeItem(STORAGE_KEYS.DTF_FILE);
    localStorage.removeItem(STORAGE_KEYS.PAYMENT_RECEIPT);
    localStorage.removeItem(STORAGE_KEYS.SHIPPING_LABEL);
    localStorage.removeItem(STORAGE_KEYS.FORM_DATA);
    localStorage.removeItem(STORAGE_KEYS.ITEMS);
    localStorage.removeItem(STORAGE_KEYS.FILE_NAMES);
    
    // Reset form
    resetForm();
    
    // Reset form modified flag
    setFormModified(false);
    
    // Close confirm modal
    setIsConfirmCloseModalOpen(false);
    
    // Call the original onClose function
    onClose();
  };

  // Handle close button click
  const handleCloseClick = () => {
    // If form is not modified, just close
    if (!formModified) {
      resetForm();
      onClose();
      return;
    }
    
    // If form is modified, show confirm dialog
    setIsConfirmCloseModalOpen(true);
  };

  const handleFileUpload = async (files: FileList, type: 'approved' | 'dtf' | 'receipt' | 'shipping') => {
    const uploadStateMap = {
      approved: 'approved_file',
      dtf: 'dtf_file',
      receipt: 'payment_receipt',
      shipping: 'shipping_label',
    };

    const storageKeyMap = {
      approved: STORAGE_KEYS.APPROVED_FILE,
      dtf: STORAGE_KEYS.DTF_FILE,
      receipt: STORAGE_KEYS.PAYMENT_RECEIPT,
      shipping: STORAGE_KEYS.SHIPPING_LABEL,
    };

    try {
      setIsUploading(prev => ({ ...prev, [type]: true }));

      const uploadedUrls: string[] = [];
      const uploadedFileNames: string[] = [];

      // Process each file in the FileList
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Create a unique file name
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
        const filePath = `${type}/${fileName}`;

        // Upload file to Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('express-samples-files')
          .upload(filePath, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('express-samples-files')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
        uploadedFileNames.push(file.name);
      }

      // Save the original file names
      setFileNames(prev => ({
        ...prev,
        [uploadStateMap[type]]: uploadedFileNames
      }));

      // Join multiple URLs with a delimiter if there are multiple files
      const urlString = uploadedUrls.join('|');

      // Update form data
      handleFormChange({
        ...formData,
        [uploadStateMap[type]]: urlString,
      });
      
      // Save to localStorage
      localStorage.setItem(storageKeyMap[type], urlString);
      
      // Save file names to localStorage
      localStorage.setItem(STORAGE_KEYS.FILE_NAMES, JSON.stringify({
        ...fileNames,
        [uploadStateMap[type]]: uploadedFileNames
      }));
    } catch (error: any) {
      console.error('Error uploading file:', error);
      setError(error.message || 'Error al subir el archivo');
      setShowErrorModal(true);
    } finally {
      setIsUploading(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleDeleteFile = (field: string) => {
    // Extract file names from URL string
    const urls = (formData[field as keyof typeof formData] as string).split('|');
    const fileNamesArray = fileNames[field] || urls.map(url => url.split('/').pop() || 'archivo');
    
    setFileToDelete({ field, fileName: fileNamesArray.join(', ') });
    setIsDeleteFileModalOpen(true);
  };

  const confirmDeleteFile = () => {
    if (!fileToDelete) return;
    
    // Update form data
    handleFormChange({
      ...formData,
      [fileToDelete.field]: '',
    });
    
    // Remove from localStorage
    const storageKeyMap: Record<string, string> = {
      approved_file: STORAGE_KEYS.APPROVED_FILE,
      dtf_file: STORAGE_KEYS.DTF_FILE,
      payment_receipt: STORAGE_KEYS.PAYMENT_RECEIPT,
      shipping_label: STORAGE_KEYS.SHIPPING_LABEL,
    };
    
    const storageKey = storageKeyMap[fileToDelete.field];
    if (storageKey) {
      localStorage.removeItem(storageKey);
    }
    
    // Remove from fileNames
    setFileNames(prev => {
      const newFileNames = {...prev};
      delete newFileNames[fileToDelete.field];
      return newFileNames;
    });
    
    // Update fileNames in localStorage
    localStorage.setItem(STORAGE_KEYS.FILE_NAMES, JSON.stringify({
      ...fileNames,
      [fileToDelete.field]: undefined
    }));
    
    setFileToDelete(null);
    setFormModified(true);
  };

  // Render file links for multiple files
  const renderFileLinks = (urls: string, field: string) => {
    if (!urls) return null;
    
    const urlArray = urls.split('|');
    const fileNamesArray = fileNames[field] || urlArray.map(url => url.split('/').pop() || 'archivo');
    
    return (
      <div className="flex flex-col gap-1">
        {urlArray.map((url, index) => (
          <a
            key={index}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300"
          >
            {fileNamesArray[index] || `Archivo ${index + 1}`}
          </a>
        ))}
      </div>
    );
  };

  // Get available product types from stock
  const getAvailableProductTypes = () => {
    return [...new Set(expressStock
      .filter(stock => stock.quantity > 0)
      .map(stock => stock.product_type)
    )];
  };

  // Get available colors for a product type
  const getAvailableColors = (productType: string) => {
    return [...new Set(expressStock
      .filter(stock => 
        stock.product_type === productType &&
        (stock.quantity > 0 || items.some(item =>
          item.product_type === stock.product_type &&
          item.color === stock.color
        ))
      )
      .map(stock => stock.color)
    )];
  };

  // Get available sizes for a product type and color
const getAvailableSizes = (productType: string, color: string) => {
  return expressStock
    .filter(stock => 
      stock.product_type === productType && 
      stock.color === color &&
      (stock.quantity > 0 || items.some(item =>
        item.product_type === stock.product_type &&
        item.color === stock.color &&
        item.size === stock.size
      ))
    )
    .map(stock => ({
      value: stock.size,
      label: `${stock.size} (${stock.quantity} disponibles)`
    }));
};


  // Get stock ID for a product type, color, and size
  const getStockId = (productType: string, color: string, size: string) => {
    const stock = expressStock.find(s => 
      s.product_type === productType && 
      s.color === color && 
      s.size === size
    );
    return stock?.id || '';
  };

const handleAddItem = () => {
  const availableProductTypes = getAvailableProductTypes();
  if (availableProductTypes.length === 0) {
    setError('No hay productos disponibles en stock');
    setShowErrorModal(true);
    return;
  }

  // Bloquear todos los ítems existentes
  const updatedItems = items.map(item => ({
    ...item,
    locked: true
  }));

  // Agregar nuevo ítem editable con valores vacíos
  const newItems = [
    ...updatedItems,
    {
      product_type: '',
      color: '',
      size: '',
      quantity: 1,
      locked: false
    }
  ];

  setItems(newItems);
  setFormModified(true);
};


  const handleUpdateItem = (index: number, field: keyof ExpressSampleItem, value: any) => {
    // If the item is locked, don't allow updates
    if (items[index].locked) {
      return;
    }
    
    // Create a new array to avoid mutating the original
    const updatedItems = [...items];
    
    // If changing product_type, reset color and size
    if (field === 'product_type') {
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
        color: '',
        size: ''
      };
    }
    // If changing color, reset size
else if (field === 'color') {
  // Verificar si hay talles disponibles
  const availableSizes = getAvailableSizes(updatedItems[index].product_type, value);
  if (availableSizes.length === 0) {
    setError(`No hay talles disponibles para ${updatedItems[index].product_type} color ${value}`);
    setShowErrorModal(true);
    return;
  }

  // Verificar si ya existe la combinación completa (aunque size aún no esté seteado)
  const itemExists = items.some((item, i) =>
    i !== index &&
    item.product_type === updatedItems[index].product_type &&
    item.color === value &&
    item.size === updatedItems[index].size // <- mantiene el size actual
  );

  if (itemExists) {
    setError(`Ya existe un item con ${updatedItems[index].product_type} color ${value} talle ${updatedItems[index].size}`);
    setShowErrorModal(true);
    return;
  }

  // Solo actualizar color, no asignar talla automáticamente
  updatedItems[index] = {
    ...updatedItems[index],
    [field]: value,
    size: '' // resetear talle explícitamente
  };
}

    // If changing size
    else if (field === 'size') {
      // Check if this combination already exists in other items
      const itemExists = items.some((item, i) => 
        i !== index && 
        item.product_type === updatedItems[index].product_type && 
        item.color === updatedItems[index].color && 
        item.size === value
      );
      
      if (itemExists) {
        setError(`Ya existe un item con ${updatedItems[index].product_type} color ${updatedItems[index].color} talle ${value}`);
        setShowErrorModal(true);
        return;
      }
      
      updatedItems[index] = { 
        ...updatedItems[index], 
        [field]: value
      };
    }
    // If changing quantity, check if there's enough stock
    else if (field === 'quantity') {
      const stockItem = expressStock.find(s => 
        s.product_type === updatedItems[index].product_type && 
        s.color === updatedItems[index].color && 
        s.size === updatedItems[index].size
      );
      
      if (stockItem && value > stockItem.quantity) {
        setError(`No hay suficiente stock disponible. Máximo: ${stockItem.quantity}`);
        setShowErrorModal(true);
        return;
      }
      
      updatedItems[index] = { 
        ...updatedItems[index], 
        [field]: value 
      };
    }
    // Otherwise just update the field
    else {
      updatedItems[index] = { 
        ...updatedItems[index], 
        [field]: value 
      };
    }
    
    setItems(updatedItems);
    setFormModified(true);
  };

  const handleRemoveItem = (index: number) => {
    // Create a new array without the item at the specified index
    const updatedItems = [...items];
    updatedItems.splice(index, 1);
    
    // If we're removing the last item and there are still items left,
    // and we're NOT editing an existing sample, unlock the new last item
    if (updatedItems.length > 0 && !sampleId) {
      updatedItems[updatedItems.length - 1].locked = false;
    }
    
    setItems(updatedItems);
    setFormModified(true);
  };

  // Calculate stock changes between original and current items
  const calculateStockChanges = () => {
    const changes: {
      stockId: string;
      quantity: number;
      type: 'in' | 'out';
    }[] = [];

    // If creating a new sample, all items are "out" movements
    if (!sampleId) {
      items.forEach(item => {
        const stockId = getStockId(item.product_type, item.color, item.size);
        if (stockId) {
          changes.push({
            stockId,
            quantity: item.quantity,
            type: 'out'
          });
        }
      });
      return changes;
    }

    // For editing, we need to compare original and current items
    
    // First, handle removed items (return to stock)
    originalItems.forEach(originalItem => {
      const stillExists = items.some(
        item => 
          item.product_type === originalItem.product_type &&
          item.color === originalItem.color &&
          item.size === originalItem.size
      );

      if (!stillExists) {
        const stockId = getStockId(originalItem.product_type, originalItem.color, originalItem.size);
        if (stockId) {
          changes.push({
            stockId,
            quantity: originalItem.quantity,
            type: 'in' // Return to stock
          });
        }
      }
    });

    // Then handle new or modified items
    items.forEach(item => {
      const originalItem = originalItems.find(
        oi => 
          oi.product_type === item.product_type &&
          oi.color === item.color &&
          oi.size === item.size
      );

      const stockId = getStockId(item.product_type, item.color, item.size);
      if (!stockId) return;

      if (!originalItem) {
        // New item - take from stock
        changes.push({
          stockId,
          quantity: item.quantity,
          type: 'out'
        });
      } else if (item.quantity !== originalItem.quantity) {
        // Quantity changed
        const diff = item.quantity - originalItem.quantity;
        if (diff > 0) {
          // Need more from stock
          changes.push({
            stockId,
            quantity: diff,
            type: 'out'
          });
        } else if (diff < 0) {
          // Return some to stock
          changes.push({
            stockId,
            quantity: Math.abs(diff),
            type: 'in'
          });
        }
      }
    });

    return changes;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isDeposito) formData.sample_value = null;

    // Validate approved file
    if (!formData.approved_file) {
      setError('Debe cargar la ficha aprobada');
      setShowErrorModal(true);
      return;
    }
    
    // Validate stock availability for all items
    for (const item of items) {
      const stockItem = expressStock.find(s => 
        s.product_type === item.product_type && 
        s.color === item.color && 
        s.size === item.size
      );
      
      // For new items or increased quantities, check stock availability
      const originalItem = originalItems.find(oi => 
        oi.product_type === item.product_type && 
        oi.color === item.color && 
        oi.size === item.size
      );
      
      let requiredQuantity = item.quantity;
      
      // If editing an existing item, only check the difference
      if (originalItem) {
        requiredQuantity = Math.max(0, item.quantity - originalItem.quantity);
      }
      
      if (!stockItem || stockItem.quantity < requiredQuantity) {
        setError(`No hay suficiente stock para ${item.product_type} ${item.color} talle ${item.size}`);
        setShowErrorModal(true);
        return;
      }
    }

    try {
      // Calculate stock changes
      const stockChanges = calculateStockChanges();

      // Remove locked property from items before saving
      const itemsToSave = items.map(({ locked, ...item }) => item);

      if (sampleId) {
        // Update existing sample
await updateExpressSample(sampleId, {
  ...formData,
  items: itemsToSave,
  file_names: JSON.stringify(fileNames),       // 👈 guarda nombres
});


        
        // Process stock changes
        for (const change of stockChanges) {
          // Create stock movement
          await addExpressStockMovement({
            stock_id: change.stockId,
            movement_type: change.type,
            quantity: change.quantity,
            express_item_id: null
          });
          
          // Update stock quantity
          const stockItem = expressStock.find(s => s.id === change.stockId);
          if (stockItem) {
            const newQuantity = change.type === 'in' 
              ? stockItem.quantity + change.quantity
              : Math.max(0, stockItem.quantity - change.quantity);
            
            // Update stock in database
            const { error } = await supabase
              .from('express_stock')
              .update({ quantity: newQuantity })
              .eq('id', change.stockId);
            
            if (error) throw error;
          }
        }
      } else {
        // Create new sample
const newSampleId = await addExpressSample(
  { ...formData, file_names: JSON.stringify(fileNames) }, // 👈 guarda nombres
  itemsToSave
);


        
        // Process stock movements for each item
        for (const change of stockChanges) {
          // Create stock movement
          await addExpressStockMovement({
            stock_id: change.stockId,
            movement_type: change.type,
            quantity: change.quantity,
            express_item_id: null
          });
          
          // Update stock quantity
          const stockItem = expressStock.find(s => s.id === change.stockId);
          if (stockItem) {
            const newQuantity = change.type === 'in' 
              ? stockItem.quantity + change.quantity
              : Math.max(0, stockItem.quantity - change.quantity);
            
            // Update stock in database
            const { error } = await supabase
              .from('express_stock')
              .update({ quantity: newQuantity })
              .eq('id', change.stockId);
            
            if (error) throw error;
          }
        }
      }
      
      // Refresh stock data after changes
      await fetchExpressStock();
      await fetchExpressSampleItems();
      
      // Clear localStorage after successful submission
      localStorage.removeItem(STORAGE_KEYS.APPROVED_FILE);
      localStorage.removeItem(STORAGE_KEYS.DTF_FILE);
      localStorage.removeItem(STORAGE_KEYS.PAYMENT_RECEIPT);
      localStorage.removeItem(STORAGE_KEYS.SHIPPING_LABEL);
      localStorage.removeItem(STORAGE_KEYS.FORM_DATA);
      localStorage.removeItem(STORAGE_KEYS.ITEMS);
      localStorage.removeItem(STORAGE_KEYS.FILE_NAMES);
      
      // Reset form
      resetForm();
      
      // Reset form modified flag
      setFormModified(false);
      
      onClose();
    } catch (error: any) {
      console.error('Error saving express sample:', error);
      setError(error.message || 'Error al guardar la muestra');
      setShowErrorModal(true);
    }
  };

  return (
    <>
      <Dialog.Root open={isOpen} onOpenChange={(open) => { if (!open) handleCloseClick(); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/50" />
          <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[600px] translate-x-[-50%] translate-y-[-50%] rounded-lg bg-white dark:bg-gray-800 p-6 shadow-lg overflow-y-auto">
            <Dialog.Title className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
              {sampleId ? 'Editar Muestra Express' : 'Nueva Muestra Express'}
            </Dialog.Title>
            
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Client Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Cliente
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1">
                      <Select
                        label=""
                        value={formData.client_id}
                        onChange={(value) => handleFormChange({ ...formData, client_id: value })}
                        options={[
                          { value: '', label: 'Seleccionar cliente...' },
                          ...clients.map(client => ({
                            value: client.id,
                            label: client.name,
                          }))
                        ]}
                        required
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setIsClientModalOpen(true)}
                      icon={<UserPlus className="h-4 w-4" />}
                      className="h-12 flex items-center justify-center"
                    >
                      Nuevo
                    </Button>
                  </div>
                </div>

                <Input
                  label="Nombre de la Muestra"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={(e) => handleFormChange({ ...formData, name: e.target.value })}
                  required
                />

                {/* Is Printed Selection */}
                <Select
                  label="Tipo"
                  value={formData.is_printed ? "true" : "false"}
                  onChange={(value) => handleFormChange({ ...formData, is_printed: value === "true" })}
                  options={[
                    { value: "false", label: "Liso" },
                    { value: "true", label: "Estampado" }
                  ]}
                  required
                />

                {/* Approved File - Required */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Ficha Aprobada <span className="text-red-500">*</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const files = e.target.files;
                        if (files && files.length > 0) handleFileUpload(files, 'approved');
                      }}
                      className="hidden"
                      id="approved-file"
                      multiple
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('approved-file')?.click()}
                      disabled={isUploading.approved}
                      icon={isUploading.approved ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      className="h-12 flex items-center justify-center"
                    >
                      Subir Ficha
                    </Button>
                    {formData.approved_file && (
                      <div className="flex items-center gap-2">
                        {renderFileLinks(formData.approved_file, 'approved_file')}
                        <button
                          type="button"
                          onClick={() => handleDeleteFile('approved_file')}
                          className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  {!formData.approved_file && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      La ficha aprobada es obligatoria
                    </p>
                  )}
                </div>

                {/* DTF File - Optional */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Archivo DTF
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const files = e.target.files;
                        if (files && files.length > 0) handleFileUpload(files, 'dtf');
                      }}
                      className="hidden"
                      id="dtf-file"
                      multiple
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('dtf-file')?.click()}
                      disabled={isUploading.dtf}
                      icon={isUploading.dtf ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      className="h-12 flex items-center justify-center"
                    >
                      Subir DTF
                    </Button>
                    {formData.dtf_file && (
                      <div className="flex items-center gap-2">
                        {renderFileLinks(formData.dtf_file, 'dtf_file')}
                        <button
                          type="button"
                          onClick={() => handleDeleteFile('dtf_file')}
                          className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* DTF Printed - Optional */}
                <Select
                  label="DTF enviado"
                  value={formData.dtf_printed || ''}
                  onChange={(value) => handleFormChange({ 
                    ...formData, 
                    dtf_printed: value ? value as 'Sí' | 'No' : null 
                  })}
                  options={[
                    { value: '', label: 'No especificado' },
                    { value: 'Sí', label: 'Sí' },
                    { value: 'No', label: 'No' },
                  ]}
                />

                {/* DTF Picked Up - Optional */}
                <Select
                  label="DTF retirado"
                  value={formData.dtf_picked_up || ''}
                  onChange={(value) => handleFormChange({ 
                    ...formData, 
                    dtf_picked_up: value ? value as 'Sí' | 'No' : null 
                  })}
                  options={[
                    { value: '', label: 'No especificado' },
                    { value: 'Sí', label: 'Sí' },
                    { value: 'No', label: 'No' },
                  ]}
                />

                <Input
                  type="date"
                  label="Fecha de Entrega"
                  id="delivery_date"
                  name="delivery_date"
                  value={formData.delivery_date}
                  onChange={(e) => handleFormChange({ ...formData, delivery_date: e.target.value })}
                  required
                />

                <Select
                  label="Vendedor"
                  value={formData.salesperson}
                  onChange={(value) => handleFormChange({ ...formData, salesperson: value as 'Mateo' | 'Male' })}
                  options={[
                    { value: 'Mateo', label: 'Mateo' },
                    { value: 'Male', label: 'Male' },
                  ]}
                  required
                />

                {/* Sample Value */}
                {!isDeposito && (
                  <Input
                    label="Valor muestra (no incluir envío)"
                    type="number"
                    id="sample_value"
                    name="sample_value"
                    step="0.01"
                    value={formData.sample_value ?? ''}
                    onChange={handleSampleValueChange}
                    required
                    placeholder="0.00"
                  />
                )}
                
                {/* Payment Receipt - Optional */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Comprobante de Pago
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const files = e.target.files;
                        if (files && files.length > 0) handleFileUpload(files, 'receipt');
                      }}
                      className="hidden"
                      id="payment-receipt"
                      multiple
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('payment-receipt')?.click()}
                      disabled={isUploading.receipt}
                      icon={isUploading.receipt ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      className="h-12 flex items-center justify-center"
                    >
                      Subir Comprobante
                    </Button>
                    {formData.payment_receipt && (
                      <div className="flex items-center gap-2">
                        {renderFileLinks(formData.payment_receipt, 'payment_receipt')}
                        <button
                          type="button"
                          onClick={() => handleDeleteFile('payment_receipt')}
                          className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Shipping Label - Optional */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Etiqueta de envío
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const files = e.target.files;
                        if (files && files.length > 0) handleFileUpload(files, 'shipping');
                      }}
                      className="hidden"
                      id="shipping-label"
                      multiple
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('shipping-label')?.click()}
                      disabled={isUploading.shipping}
                      icon={isUploading.shipping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      className="h-12 flex items-center justify-center"
                    >
                      Subir Etiqueta
                    </Button>
                    {formData.shipping_label && (
                      <div className="flex items-center gap-2">
                        {renderFileLinks(formData.shipping_label, 'shipping_label')}
                        <button
                          type="button"
                          onClick={() => handleDeleteFile('shipping_label')}
                          className="text-gray-400 hover:text-red-500 dark:hover:text-red-400"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Items */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">Items</h3>

                  </div>

                  {items.map((item, index) => (
                    <div 
                      key={index} 
                      className={`grid grid-cols-12 gap-2 items-start border ${
                        item.locked 
                          ? 'border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50' 
                          : 'border-gray-200 dark:border-gray-700'
                      } p-4 rounded-lg`}
                    >
                      <div className="col-span-3">
<Select
  label="Tipo"
  value={item.product_type}
  onChange={(value) => handleUpdateItem(index, 'product_type', value)}
  options={[
    { value: '', label: 'Seleccione tipo' },
    ...getAvailableProductTypes().map(type => ({
      value: type,
      label: type
    }))
  ]}
  required
  disabled={item.locked}
/>

                      </div>

                      <div className="col-span-3">
                        <Select
                          label="Color"
                          value={item.color}
                          onChange={(value) => handleUpdateItem(index, 'color', value)}
                          options={item.product_type ? 
                            getAvailableColors(item.product_type).map(color => ({
                              value: color,
                              label: color
                            })) : []
                          }
                          required
                          disabled={item.locked || !item.product_type}
                        />
                      </div>

                      <div className="col-span-3">
                        <Select
                          label="Talle"
                          value={item.size}
                          onChange={(value) => handleUpdateItem(index, 'size', value as 'S' | 'M' | 'L' | 'XL')}
                          options={item.product_type && item.color ? 
                            getAvailableSizes(item.product_type, item.color) : []
                          }
                          required
                          disabled={item.locked || !item.product_type || !item.color}
                        />
                      </div>

                      <div className="col-span-2">
                        <Input
                          label="Cantidad"
                          type="number"
                          id={`quantity-${index}`}
                          name={`quantity-${index}`}
                          value={item.quantity.toString()}
                          onChange={(e) => handleUpdateItem(index, 'quantity', parseInt(e.target.value))}
                          min="1"
                          required
                          disabled={item.locked}
                        />
                      </div>

                      <div className="col-span-1">
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index)}
                          className="mt-7 p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 focus:outline-none"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                    <Button
                      variant="outline"
                      size="sm"
                      icon={<Plus className="h-4 w-4" />}
                      onClick={handleAddItem}
                    >
                      Agregar Item
                    </Button>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Comentarios
                  </label>
                  <textarea
                    id="comment"
                    name="comment"
                    rows={3}
                    value={formData.comment}
                    onChange={(e) => handleFormChange({ ...formData, comment: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 dark:bg-gray-700 dark:text-white sm:text-sm"
                    placeholder="Agregar comentarios adicionales..."
                  />
                </div>

                {sampleId && (
                  <Select
                    label="Estado"
                    value={formData.status}
                    onChange={(value) => handleFormChange({ ...formData, status: value as ExpressSample['status'] })}
                    options={[
                      { value: 'pending', label: 'Pendiente' },
                      { value: 'in_progress', label: 'En Proceso' },
                      { value: 'finished', label: 'Terminado' },
                      { value: 'shipped', label: 'Despachado' },
                    ]}
                    required
                  />
                )}
              </div>
              
              <div className="mt-6 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleCloseClick}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={!isFormValid()}
                >
                  {sampleId ? 'Guardar Cambios' : 'Crear Muestra'}
                </Button>
              </div>
            </form>
            
<Dialog.Close asChild>
  <button
    type="button"
    className="absolute top-4 right-4 text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
    aria-label="Close"
    onClick={(e) => {
      e.preventDefault();
      handleCloseClick();
    }}
  >
    <X className="h-6 w-6" />
  </button>
</Dialog.Close>

          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {isClientModalOpen && (
        <EntityModal
          isOpen={isClientModalOpen}
          onClose={() => setIsClientModalOpen(false)}
          entityType="client"
          onSave={async (client) => {
            try {
              const newClient = await addClient(client);
              handleFormChange({ ...formData, client_id: newClient.id });
              setIsClientModalOpen(false);
              setFormModified(true);
            } catch (error) {
              console.error('Error creating client:', error);
              setError('Error al crear el cliente');
              setShowErrorModal(true);
            }
          }}
        />
      )}

      <ConfirmCloseModal
        isOpen={isConfirmCloseModalOpen}
        onClose={() => setIsConfirmCloseModalOpen(false)}
        onConfirm={handleConfirmClose}
      />

      <ErrorModal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error || 'Ha ocurrido un error'}
      />

      <ConfirmModal
        isOpen={isDeleteFileModalOpen}
        onClose={() => {
          setIsDeleteFileModalOpen(false);
          setFileToDelete(null);
        }}
        onConfirm={confirmDeleteFile}
        title="Eliminar archivo"
        message={`¿Estás seguro de que querés eliminar el archivo ${fileToDelete?.fileName}?`}
        confirmText="Eliminar"
        variant="danger"
      />
    </>
  );
};

export default ExpressSampleModal;